import Typography from '~/components/Typography.tsx';
import MuiBox from '@mui/material/Box';
import { Banner } from '~kup/models/Advertisement.ts';
import { CSSProperties, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export type BannerProps = {
  advertisementSlug: string;
  advertisementCategory: string;
  banner: Banner;
  onClick?: (e: React.SyntheticEvent) => void;
  hideImage?: boolean;
};

export default function BannerDisplay(props: BannerProps) {
  const banner = props.banner;
  const navigate = useNavigate();
  const location = useLocation();
  const handleClickBanner = (e: React.SyntheticEvent) => {
    e.preventDefault();
    props.onClick?.(e);
    if (!banner.url) return;
    if (banner.url?.startsWith('http')) {
      window.open(banner.url, '_blank')?.focus();
    } else if (location.pathname !== banner.url) {
      navigate(banner.url);
    }
  };
  switch (banner?.type?.toLowerCase()) {
    case 'basic':
      return (
        <BasicBanner banner={banner} onClick={handleClickBanner} hideImage={props.hideImage} />
      );
    case 'thin':
      return <ThinBanner banner={banner} onClick={handleClickBanner} hideImage={props.hideImage} />;
    case 'square':
      return (
        <SquareBanner banner={banner} onClick={handleClickBanner} hideImage={props.hideImage} />
      );
    case 'lean':
    default:
      return null;
  }
}

type BasicBannerProps = {
  banner: Banner;
  onClick: (e: React.SyntheticEvent) => void;
  hideImage?: boolean;
};
type SquareBannerProps = BasicBannerProps;
type ThinBannerProps = BasicBannerProps;

export function BasicBanner(props: BasicBannerProps) {
  const banner = props.banner;
  const backgroundValue =
    banner.background?.startsWith('http') || banner.background?.startsWith('data')
      ? `url(${banner.background})`
      : banner.background;

  const [isLoading, setIsLoading] = useState<boolean>(props.hideImage ? false : !!banner.image);
  const [isBGLoading, setIsBGLoading] = useState<boolean>(banner.background?.startsWith('http'));

  const backgroundStyle: CSSProperties = {
    background: `${backgroundValue}`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    ...banner.backgroundStyle,
  };
  const imageStyle: CSSProperties = {
    height: '100%',
    objectFit: 'contain',
    ...banner.imageStyle,
  };
  const titleStyle: CSSProperties = {
    width: '100%',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'pre-wrap',
    lineHeight: 1.5,
    fontSize: !banner.image ? 'min(5vw, 24px)' : 'min(4.2vw, 24px)',
    ...banner.titleStyle,
  };
  const descriptionStyle: CSSProperties = {
    width: '100%',
    whiteSpace: 'pre-wrap',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    lineHeight: 1.4,
    fontSize: !banner.image ? 'min(3.3vw, 18px)' : 'min(3vw, 16px)',
    ...banner.descriptionStyle,
  };
  const overrideStyle: CSSProperties = {
    ...banner.overrideStyle,
  };
  return (
    <MuiBox
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        height: '80px',
        position: 'relative',
        padding: '6px 12px',
        cursor: 'pointer',
        ...backgroundStyle,
        ...overrideStyle,
      }}
      onClick={props.onClick}
    >
      {!props.hideImage && banner.background?.startsWith('http') && (
        <img
          style={{ width: '0px', height: '0px' }}
          src={banner.background}
          onLoad={() => {
            setIsBGLoading(false);
          }}
        />
      )}
      {banner.image && (
        <MuiBox
          sx={{
            display: 'inline-flex',
            height: '68px',
            maxWidth: '20vw !important',
            overflow: 'hidden',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={banner.image}
            alt={banner.title}
            style={imageStyle}
            onLoad={() => {
              setIsLoading(false);
            }}
          />
        </MuiBox>
      )}
      {!isLoading && !isBGLoading && (banner.title || banner.description) && (
        <MuiBox
          sx={{
            display: 'inline-flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
          }}
        >
          {banner.title && (
            <Typography component={'h3'} variant={'BaseM_B'} sx={titleStyle}>
              {banner.title}
            </Typography>
          )}

          {banner.description && (
            <Typography variant={'Caption'} component={'p'} sx={descriptionStyle}>
              {banner.description}
            </Typography>
          )}
        </MuiBox>
      )}
    </MuiBox>
  );
}

export function ThinBanner(props: ThinBannerProps) {
  const banner = props.banner;
  const backgroundValue =
    banner.background?.startsWith('http') || banner.background?.startsWith('data')
      ? `url(${banner.background})`
      : banner.background;
  const [isLoading, setIsLoading] = useState<boolean>(props.hideImage ? false : !!banner.image);
  const [isBGLoading, setIsBGLoading] = useState<boolean>(banner.background?.startsWith('http'));
  const backgroundStyle = {
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    ...banner.backgroundStyle,
  };
  const imageStyle: CSSProperties = {
    ...banner.imageStyle,
  };
  const overrideStyle: CSSProperties = {
    ...banner.overrideStyle,
  };
  const titleStyle: CSSProperties = {
    width: '100%',
    textAlign: 'left',
    lineHeight: 1.2,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    fontSize: 'min(4.25vw, 18px)',
    whiteSpace: 'pre-wrap',
    ...banner.titleStyle,
  };
  const descriptionStyle: CSSProperties = {
    width: '100%',
    whiteSpace: 'pre-wrap',
    textAlign: 'left',
    fontSize: 'min(3vw, 14px)',
    lineHeight: 1.5,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    ...banner.descriptionStyle,
  };
  return (
    <MuiBox
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
        height: '60px',
        position: 'relative',
        background: `${backgroundValue}`,
        padding: '4px 8px',
        cursor: 'pointer',
        ...backgroundStyle,
        ...overrideStyle,
      }}
      onClick={props.onClick}
    >
      {banner.background?.startsWith('http') && (
        <img
          style={{ width: '0px', height: '0px' }}
          src={banner.background}
          onLoad={() => {
            setIsBGLoading(false);
          }}
        />
      )}
      {!props.hideImage && banner.image && (
        <MuiBox
          sx={{
            display: 'inline-flex',
            height: '52px',
            maxWidth: '20vw !important',
          }}
        >
          <img
            src={banner.image}
            alt={banner.title}
            style={imageStyle}
            onLoad={() => {
              setIsLoading(false);
            }}
          />
        </MuiBox>
      )}
      {!isLoading && !isBGLoading && (banner.title || banner.description) && (
        <MuiBox
          sx={{
            display: 'inline-flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
          }}
        >
          {banner.title && (
            <Typography
              component={'h3'}
              variant={'BaseM_B'}
              sx={titleStyle}
              color={'typography.primary'}
            >
              {banner.title}
            </Typography>
          )}

          {banner.description && (
            <Typography
              variant={'Caption'}
              component={'p'}
              sx={descriptionStyle}
              color={'typography.primary'}
            >
              {banner.description}
            </Typography>
          )}
        </MuiBox>
      )}
    </MuiBox>
  );
}

export function SquareBanner(props: SquareBannerProps) {
  const banner = props.banner;
  const backgroundValue =
    banner.background?.startsWith('http') || banner.background?.startsWith('data')
      ? `url(${banner.background})`
      : banner.background;
  const [isLoading, setIsLoading] = useState<boolean>(props.hideImage ? false : !!banner.image);
  const [isBGLoading, setIsBGLoading] = useState<boolean>(banner.background?.startsWith('http'));
  const backgroundStyle = {
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    ...banner.backgroundStyle,
  };
  const imageStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    ...banner.imageStyle,
  };
  const overrideStyle = {
    ...banner.overrideStyle,
  };
  const titleStyle = {
    ...banner.titleStyle,
  };
  const descriptionStyle = {
    ...banner.descriptionStyle,
  };
  return (
    <MuiBox
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: 'calc((100vw - 40px) * 0.93)',
        background: backgroundValue,
        cursor: 'pointer',
        ...backgroundStyle,
        ...overrideStyle,
      }}
      onClick={props.onClick}
    >
      {!props.hideImage && banner.background?.startsWith('http') && (
        <img
          style={{ width: '0px', height: '0px' }}
          src={banner.background}
          onLoad={() => {
            setIsBGLoading(false);
          }}
        />
      )}
      <MuiBox
        sx={{
          height: '50%',
          flexShrink: 0,
          width: '100%',
          padding: '20px 20px 0px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {banner.image && (
          <img
            src={banner.image}
            alt={banner.title}
            style={imageStyle}
            onLoad={() => {
              setIsLoading(false);
            }}
          />
        )}
      </MuiBox>

      {!isBGLoading && !isLoading && !!(banner.title || banner.description) && (
        <MuiBox
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            flexDirection: 'column',
            flex: 1,
            flexShrink: 0,
            height: '50%',
            width: '100%',
            background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.0) 0%, rgba(0, 0, 0, 0.5) 50%)',
            padding: '10vw 6vw',
          }}
        >
          <Typography
            component={'h3'}
            variant={'TitleS_B'}
            sx={{
              color: 'white',
              textAlign: 'center',
              textOverflow: 'ellipsis',
              fontSize: 'min(5vw, 32px)',
              lineHeight: 1.5,
              mb: '4px',
              whiteSpace: 'pre-wrap',
              overflow: 'hidden',
              textShadow: '0px 0px 4px rgba(0, 0, 0, 1)',
              ...titleStyle,
            }}
          >
            {banner.title}
          </Typography>
          <Typography
            variant={'FootNote'}
            component={'p'}
            sx={{
              color: 'white',
              textAlign: 'center',
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.5,
              textShadow: '0px 0px 4px rgba(0, 0, 0, 1)',
              fontSize: 'min(4vw, 24px)',
              ...descriptionStyle,
            }}
          >
            {banner.description}
          </Typography>
        </MuiBox>
      )}
    </MuiBox>
  );
}
